/**
 * Composable implementing the application back button behavior.
 * Unlike the browser back button, the navigations between the tabs of a page
 * are ignored: the jump targets the previous page atomically. When no
 * previous page is known (new browser tab, page reload or shared link), the
 * `backTo` route meta is used as fallback target.
 */
import { storeToRefs } from "pinia";
import { computed } from "vue";
import type { RouteLocationRaw, RouteMeta } from "vue-router";
import { useRoute, useRouter } from "vue-router";
import { usePageHistoryStore } from "~/core/stores/usePageHistoryStore";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import { PATH_BUSINESS_UNITS } from "~/modules/businessUnit/constants";

/**
 * Resolve the `backTo` route meta into a back target. The function form
 * allows a target depending on the session, e.g. the entity type of the
 * current user.
 */
function resolveBackTo(
  backTo: RouteMeta["backTo"],
): RouteLocationRaw | undefined {
  return typeof backTo === "function" ? backTo() : backTo;
}

export function useAppBack() {
  const route = useRoute();
  const router = useRouter();

  /**
   * True when the application back button must be displayed on the current
   * route: the `backTo` meta resolves to a target.
   */
  const isBackButtonShown = computed(
    () => resolveBackTo(route.meta.backTo) !== undefined,
  );

  /**
   * Go back to the previous page, ignoring the tab navigations.
   * Falls back to the `backTo` meta target when no previous page is known.
   * Does nothing if no previous page is known and the `backTo` meta does not
   * resolve to a target.
   */
  async function goBack() {
    const position = router.options.history.state?.position;

    if (typeof position === "number") {
      const previousPage = usePageHistoryStore().getPreviousPage(
        route,
        position,
      );

      if (previousPage) {
        router.go(previousPage.position - position);
        return;
      }
    }

    const backTo = resolveBackTo(route.meta.backTo);

    if (backTo) {
      await router.push(backTo);
    }
  }

  /**
   * Back target of the application back button on the organization settings
   * page: the business units page, displayed for the users of a super
   * administration or a business unit only.
   */
  function getBackToBusinessUnitsRoute(): RouteLocationRaw | undefined {
    const { userSession } = storeToRefs(useUserSession());

    return userSession.value?.entity?.type ===
      UserSessionEntityType.SUPER_ADMINISTRATION ||
      userSession.value?.entity?.type === UserSessionEntityType.BUSINESS_UNIT
      ? `${PATH_BUSINESS_UNITS}/${userSession?.value.entity.code}`
      : undefined;
  }

  return {
    goBack,
    isBackButtonShown,
    getBackToBusinessUnitsRoute,
  };
}
